import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import api from '../api';
import { useT, appLocale } from '../i18n';

/**
 * External-chat lead bell: USER-LEVEL unread aggregate (all assistants), per
 * the agreed design — the user cares "is there a new lead", not whose it is.
 * Each item names its assistant; clicking navigates to that assistant's
 * 助理外派 page, and AssistantRouteScope switches the active assistant to match.
 */
export default function NotificationCenter() {
  const t = useT();
  const navigate = useNavigate();
  const [unread, setUnread] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = () => {
    api.get('/external-chat/leads')
      .then(res => setUnread((res.data?.leads || []).filter(l => !l.read_at)))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (unread.length === 0 && !open) return null;

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium hover:bg-[var(--bg-hover)]"
        style={{ color: 'var(--text-secondary)' }}
        aria-label={t('notif.leads_title')}
      >
        <Bell size={17} />
        <span>{t('notif.leads_title')}</span>
        {unread.length > 0 && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: '#22c55e' }}>
            {unread.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-64 rounded-lg border shadow-lg z-50 max-h-72 overflow-y-auto"
          style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border)' }}>
          {unread.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>{t('notif.no_new_leads')}</p>
          ) : unread.map(l => (
            <button
              key={l.id}
              onClick={() => { setOpen(false); navigate(`/assistant/${l.assistant_public_id}/deployment`); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-hover)] border-b last:border-b-0"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>{l.contact_value}</span>
                <span className="flex-none" style={{ color: 'var(--text-muted)' }}>
                  {new Date(l.created_at).toLocaleDateString(appLocale())}
                </span>
              </div>
              <div className="mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {t('notif.lead_from', { name: l.assistant_name })}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
